import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useUserSettings } from '../../modules/admin/settings/useData';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { ChevronLeftIcon, EraserIcon, SearchIcon, SpinnerIcon } from './Icons';
import { ListEmptyState, type ListEmptyStateProps } from './ListEmptyState';
import { ListPagination, type ListPaginationProps } from './ListPagination';
import { SegmentedControl, type SegmentedControlOption } from './SegmentedControl';

export type GeneralListTableDensity = 'compact' | 'normal' | 'comfortable';

/** Uniform horizontal gutter between GeneralList table columns (th/td). */
export const GENERAL_LIST_CELL_X = 'px-3';
/** Header cell vertical + horizontal padding (normal density). */
export const GENERAL_LIST_TH = 'px-3 py-3';
/** Body cell vertical + horizontal padding (normal density). */
export const GENERAL_LIST_TD = 'px-3 py-3.5';
/** Default table shell for GeneralList bodies. */
export const GENERAL_LIST_TABLE = 'w-full table-fixed text-sm';

const DENSITY_TABLE_GUTTERS: Record<GeneralListTableDensity, string> = {
  compact: '[&_table_th]:!px-3 [&_table_th]:!py-2 [&_table_td]:!px-3 [&_table_td]:!py-1.5',
  normal: '[&_table_th]:!px-3 [&_table_th]:!py-3 [&_table_td]:!px-3 [&_table_td]:!py-3.5',
  comfortable: '[&_table_th]:!px-4 [&_table_th]:!py-4 [&_table_td]:!px-4 [&_table_td]:!py-4',
};

/**
 * Account-settings table prefs (default page size + row density) for GeneralList pages.
 */
export function useGeneralListPreferences(): {
  defaultPageSize: number;
  pageSizeOptions: number[];
  tableDensity: GeneralListTableDensity;
} {
  const { userSettings } = useUserSettings();
  const { appConfig } = useSettings();

  return useMemo(() => {
    const density = userSettings?.tableDensity;
    const tableDensity: GeneralListTableDensity =
      density === 'compact' || density === 'comfortable' || density === 'normal'
        ? density
        : (appConfig.table.defaultDensity as GeneralListTableDensity);

    const rawSize = userSettings?.defaultPageSize ?? appConfig.table.defaultPageSize;
    const defaultPageSize =
      typeof rawSize === 'number' && Number.isFinite(rawSize) && rawSize > 0
        ? rawSize
        : appConfig.table.defaultPageSize;

    return {
      defaultPageSize,
      pageSizeOptions: appConfig.table.pageSizeOptions,
      tableDensity,
    };
  }, [userSettings?.defaultPageSize, userSettings?.tableDensity, appConfig.table]);
}

/**
 * Page-size state seeded from account settings (and kept in sync when prefs change).
 */
export function useGeneralListPageSize(): {
  itemsPerPage: number;
  setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
  pageSizeOptions: number[];
  defaultPageSize: number;
  tableDensity: GeneralListTableDensity;
} {
  const prefs = useGeneralListPreferences();
  const [itemsPerPage, setItemsPerPage] = useState(prefs.defaultPageSize);

  useEffect(() => {
    setItemsPerPage(prefs.defaultPageSize);
  }, [prefs.defaultPageSize]);

  return {
    itemsPerPage,
    setItemsPerPage,
    pageSizeOptions: prefs.pageSizeOptions,
    defaultPageSize: prefs.defaultPageSize,
    tableDensity: prefs.tableDensity,
  };
}

export type GeneralListSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
  className?: string;
  /** Fired when the user presses Enter in the search field. */
  onSubmit?: () => void;
};

export type GeneralListSortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

export type GeneralListSortingProps = {
  sortConfig: GeneralListSortConfig;
  onSort: (key: string) => void;
};

type GeneralListSortContextValue = GeneralListSortingProps;

const GeneralListSortContext = createContext<GeneralListSortContextValue | null>(null);

export function useGeneralListSortContext(): GeneralListSortContextValue | null {
  return useContext(GeneralListSortContext);
}

/**
 * Always-active column sort for GeneralList pages (asc ↔ desc; never clears).
 * Prefer this over `useSorting` so the default column stays highlighted and clickable.
 */
export function useGeneralListSort(
  defaultSort: GeneralListSortConfig,
  options?: {
    /** Direction applied when switching to a new column. Defaults to `asc`. */
    defaultDirectionForKey?: (key: string) => 'asc' | 'desc';
  },
): {
  sortConfig: GeneralListSortConfig;
  handleSort: (key: string) => void;
  setSortConfig: React.Dispatch<React.SetStateAction<GeneralListSortConfig>>;
} {
  const [sortConfig, setSortConfig] = useState<GeneralListSortConfig>(defaultSort);

  const defaultDirectionForKey = options?.defaultDirectionForKey;

  const handleSort = useCallback(
    (key: string) => {
      setSortConfig((current) => {
        if (current.key === key) {
          return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
        }
        return {
          key,
          direction: defaultDirectionForKey?.(key) ?? 'asc',
        };
      });
    },
    [defaultDirectionForKey],
  );

  return { sortConfig, handleSort, setSortConfig };
}

export type GeneralListSortableThProps = {
  sortKey: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  align?: 'left' | 'right' | 'center';
  /** Override context (rarely needed). */
  sortConfig?: GeneralListSortConfig;
  onSort?: (key: string) => void;
};

function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction?: 'asc' | 'desc';
}) {
  const { colors } = useTheme();
  if (!active) {
    return (
      <svg
        className={cn('h-3.5 w-3.5 opacity-40', colors.textMuted)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        />
      </svg>
    );
  }
  if (direction === 'asc') {
    return (
      <svg
        className={cn('h-3.5 w-3.5', colors.primaryFg)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  }
  return (
    <svg
      className={cn('h-3.5 w-3.5', colors.primaryFg)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/** Sortable column header for tables rendered inside GeneralList. Requires `sorting` on GeneralList (or explicit props). */
export function GeneralListSortableTh({
  sortKey,
  children,
  className,
  style,
  align = 'left',
  sortConfig: sortConfigProp,
  onSort: onSortProp,
}: GeneralListSortableThProps) {
  const { colors } = useTheme();
  const ctx = useGeneralListSortContext();
  const sortConfig = sortConfigProp ?? ctx?.sortConfig;
  const onSort = onSortProp ?? ctx?.onSort;

  if (!sortConfig || !onSort) {
    return (
      <th
        className={cn('sticky top-0 z-10 font-medium', colors.surface, colors.textMuted, className)}
        style={style}
      >
        {children}
      </th>
    );
  }

  const active = sortConfig.key === sortKey;

  return (
    <th className={cn('sticky top-0 z-10 font-medium', colors.surface, className)} style={style}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide',
          align === 'right' && 'w-full justify-end',
          align === 'center' && 'w-full justify-center',
          active ? colors.primaryFg : cn(colors.textMuted, 'hover:opacity-90'),
        )}
      >
        {children}
        <SortIcon active={active} direction={active ? sortConfig.direction : undefined} />
      </button>
    </th>
  );
}

export type GeneralListProps<T extends string = string> = {
  onBack?: () => void;
  /** Optional page title shown in the header (e.g. when not using tabs). */
  title?: React.ReactNode;
  description?: React.ReactNode;
  tabs?: Array<SegmentedControlOption<T>>;
  activeTab?: T;
  onTabChange?: (value: T) => void;
  /** Extra content inside the segmented control (e.g. Bulk import link). */
  tabTrailing?: React.ReactNode;
  /** Content to the left of tabs (after back / title). */
  headerStart?: React.ReactNode;
  search?: GeneralListSearchProps;
  /** Primary actions next to search (e.g. New document). */
  actions?: React.ReactNode;
  filters?: React.ReactNode;
  /**
   * When true, filters render on the same row as tabs/search
   * (after tabs on the left) instead of a second toolbar row.
   */
  filtersInline?: boolean;
  /** Optional banner below the header toolbar (errors, alerts). */
  banner?: React.ReactNode;
  resultSummary?: React.ReactNode;
  hasFilters?: boolean;
  onClearFilters?: () => void;
  clearFiltersLabel?: string;
  isLoading?: boolean;
  loadingLabel?: string;
  isEmpty?: boolean;
  emptyState?: ListEmptyStateProps;
  /** Table or other list body when there is data. */
  children?: React.ReactNode;
  pagination?: ListPaginationProps;
  className?: string;
  /** Column sorting for nested `GeneralListSortableTh` headers. */
  sorting?: GeneralListSortingProps;
  /** Override account-settings row density. */
  density?: GeneralListTableDensity;
};

export function GeneralListSearch({
  value,
  onChange,
  placeholder = 'Search…',
  title,
  className,
  onSubmit,
}: GeneralListSearchProps) {
  const { colors } = useTheme();

  return (
    <div className={cn('relative', className)}>
      <SearchIcon
        className={cn(
          'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
          colors.textMuted,
        )}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSubmit) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={placeholder}
        title={title}
        className={cn(
          'h-10 w-56 rounded-xl border py-0 pl-9 pr-3 text-sm outline-none transition box-border focus:ring-2 focus:ring-indigo-500/25 sm:w-64',
          colors.surface,
          colors.border,
          colors.text,
        )}
      />
    </div>
  );
}

export function GeneralList<T extends string = string>({
  onBack,
  title,
  description,
  tabs,
  activeTab,
  onTabChange,
  tabTrailing,
  headerStart,
  search,
  actions,
  filters,
  filtersInline = false,
  banner,
  resultSummary,
  hasFilters = false,
  onClearFilters,
  clearFiltersLabel = 'Clear filters',
  isLoading = false,
  loadingLabel = 'Loading…',
  isEmpty = false,
  emptyState,
  children,
  pagination,
  className,
  sorting,
  density: densityProp,
}: GeneralListProps<T>) {
  const { colors } = useTheme();
  const prefs = useGeneralListPreferences();
  const tableDensity = densityProp ?? prefs.tableDensity;
  const tableGutters = DENSITY_TABLE_GUTTERS[tableDensity] ?? DENSITY_TABLE_GUTTERS.normal;

  const clearFiltersControl =
    hasFilters && onClearFilters ? (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onClearFilters}
        aria-label={clearFiltersLabel}
        title={clearFiltersLabel}
        className="shrink-0"
      >
        <EraserIcon className="h-4 w-4" />
      </Button>
    ) : null;

  const body = (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('relative z-30 flex flex-shrink-0 flex-col gap-2.5 border-b px-4 py-3 sm:px-5', colors.border)}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {onBack && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onBack}
                  aria-label="Back"
                  title="Back"
                  className="shrink-0"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </Button>
              )}
              {(title != null || description != null) && (
                <div className="min-w-0 max-w-xl">
                  {title != null && (
                    <h2 className={cn('text-base font-semibold leading-tight', colors.text)}>{title}</h2>
                  )}
                  {description != null && (
                    <p className={cn('mt-0.5 text-xs sm:text-sm leading-snug', colors.textMuted)}>{description}</p>
                  )}
                </div>
              )}
              {headerStart}
              {tabs && activeTab != null && onTabChange && (
                <SegmentedControl
                  options={tabs}
                  value={activeTab}
                  onChange={onTabChange}
                  trailing={tabTrailing}
                  className="shrink-0"
                  size="sm"
                />
              )}
              {filtersInline && filters != null ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2">{filters}</div>
              ) : null}
              {filtersInline ? clearFiltersControl : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {search && <GeneralListSearch {...search} />}
              {!filtersInline ? clearFiltersControl : null}
              {actions}
            </div>
          </div>

          {banner != null && banner}

          {!filtersInline && (filters != null || resultSummary != null) && (
            <div className="flex flex-wrap items-center gap-2">
              {filters != null && <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{filters}</div>}
              {resultSummary != null ? (
                <div className={cn('flex flex-wrap items-center gap-2', filters != null ? 'ml-auto' : '')}>
                  {resultSummary}
                </div>
              ) : null}
            </div>
          )}
          {filtersInline && resultSummary != null ? (
            <div className="flex flex-wrap items-center gap-2">{resultSummary}</div>
          ) : null}
        </div>

        {isLoading ? (
          <div className={cn('flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20', colors.textMuted)}>
            <SpinnerIcon className="h-6 w-6" />
            <p className="text-sm">{loadingLabel}</p>
          </div>
        ) : isEmpty && emptyState ? (
          <ListEmptyState {...emptyState} />
        ) : (
          <>
            <div className={cn('min-h-0 flex-1 overflow-auto', tableGutters)}>
              {children}
            </div>
            {pagination && <ListPagination {...pagination} />}
          </>
        )}
      </div>
    </div>
  );

  if (!sorting) return body;

  return (
    <GeneralListSortContext.Provider value={sorting}>
      {body}
    </GeneralListSortContext.Provider>
  );
}
